import React, {
  useState,
  useEffect,
  useContext,
  useCallback,
  useRef
} from 'react';
import { Link } from 'react-router-dom';
import {
  Grid,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Tooltip,
  TextField,
  InputAdornment,
  IconButton
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import axios from 'src/axios';
import { APICONSTANTS, CONSTANTS } from 'src/components/Constants';
import { SnackContext } from 'src/store/ContextStore';
import { SgAccessContext } from 'src/components/manage-users/SgAcessProvider';
import { copyToClipBoard } from 'src/util/util';
import { useAutoTranslation } from 'src/hooks/useTranslate';
import TextInput from 'src/components/UI/input/TextInput';
import FormLabel from 'src/components/UI/input/CustomFormLabel';
import useSpecialties from 'src/components/generate-link/useSpecialties';
import { createUniqueLink } from 'src/components/generate-link/TableActions';
import { validatePedigreeConfig } from 'src/components/pedigree-chart/pedigree-tool/pedigree/utils';
import ScreeningSummary from './ScreeningSummary';

// `label` is the stored status ('Not Sent', 'Applied' …): it picks the colours
// as it is, and is translated only to show (TGCLOUD-1893).
const StatusChip = ({ label }) => {
  const t = useAutoTranslation();
  const colors =
    CONSTANTS.fhqStatusColors[label] ||
    CONSTANTS.fhqStatusColors[CONSTANTS.fhqStatus.NOT_SENT];
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        height: 24,
        px: 1.25,
        borderRadius: '100px',
        fontSize: 12,
        fontWeight: 600,
        lineHeight: 1,
        bgcolor: colors.bg,
        color: colors.fg
      }}
    >
      {t(label)}
    </Box>
  );
};
// Opens the read-only view of what the patient submitted (ViewProbandInfo — ConfirmDetailsPanel with
// the ViewOnly* family/pregnancy tables).
//
// The button must BE the link, not sit inside one. This was previously a <Button> nested in a <Link>,
// i.e. a <button> inside an <a> — invalid HTML that silently does nothing. Per the DOM spec, a click's
// activation target is the nearest activatable element *including the target itself*: a <button> has
// activation behaviour, so it becomes the activation target and the ancestor <a> is never activated,
// so no navigation happens. Hovering still showed the browser's "Open … in a new tab" hint (that is
// the anchor), which is exactly why this looked correctly wired but never worked. `component={Link}`
// renders a single <a> — the idiom already used in MergeFamilyModal and MigrateModal.
const ViewSubmissionButton = ({ to, label, fullWidth = false }) => (
  <Button
    component={Link}
    to={to}
    target="_blank"
    rel="noopener noreferrer"
    variant="outlined"
    fullWidth={fullWidth}
    sx={{ textDecoration: 'none' }}
  >
    {label}
  </Button>
);

const FamilyHistoryMenu = ({ user, active, refreshKey }) => {
  const t = useAutoTranslation();
  const probandId = user?._id || user?.user_id;
  const { setSnack } = useContext(SnackContext);
  const { allow, readOnly: roFn } = useContext(SgAccessContext);
  const fhqAllowed = allow('fhqs');
  const fhqReadOnly = roFn('fhqs');
  const canSend = fhqAllowed && !fhqReadOnly;

  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [generatedLink, setGeneratedLink] = useState('');
  const [copied, setCopied] = useState(false);
  const autoAppliedRef = useRef(false);
  const [autoApplyFailed, setAutoApplyFailed] = useState(false);
  const { optionKeys: specialtyOptions, labelMap: specialtyLabels } =
    useSpecialties();
  const [linkParameters, setLinkParameters] = useState([]);
  const specialtiesTouchedRef = useRef(false);
  // "Copied!" resets on a timer, so it must not outlive the component — the same
  // uncleared-setTimeout-holding-a-state-setter defect as TGCLOUD-1710. Clearing
  // before re-arming also means a second copy restarts the full second rather
  // than inheriting the first timer's remainder.
  const copiedTimerRef = useRef(null);

  const fetchStatus = useCallback(() => {
    if (!probandId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    axios
      .get(APICONSTANTS.getProbandFhqStatus.replace(':probandId', probandId))
      .then((res) => setStatus(res.data))
      .catch(() => setStatus(null))
      .finally(() => setLoading(false));
  }, [probandId]);

  useEffect(() => () => clearTimeout(copiedTimerRef.current), []);

  useEffect(() => {
    if (active === undefined || active) fetchStatus();
  }, [active, fetchStatus]);

  useEffect(() => {
    if (refreshKey) fetchStatus();
  }, [refreshKey, fetchStatus]);

  // Prefill the specialty picker with what the questionnaire would actually use
  // if sent right now: a link that has already been issued keeps the specialties
  // it was issued with (a resend must not silently change the questionnaire the
  // patient sees), and anything else falls back to the user group's default
  // specialties — the same default the Generate Link screen prefills. Once the
  // clinician touches the picker their choice wins and nothing overwrites it.
  useEffect(() => {
    if (!status || specialtiesTouchedRef.current) return;
    if (Array.isArray(status.linkParameters) && status.linkParameters.length) {
      setLinkParameters(status.linkParameters);
      return;
    }
    let cancelled = false;
    axios
      .get(APICONSTANTS.getDefaultLinkParameter)
      .then((res) => {
        if (cancelled || specialtiesTouchedRef.current) return;
        const defaults = res.data?.defaultLinkParameters;
        if (res.data?.success && Array.isArray(defaults))
          setLinkParameters(defaults);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [status]);

  const handleSpecialtyChange = (e) => {
    specialtiesTouchedRef.current = true;
    setLinkParameters(e.target.value);
  };

  const issue = (withEmail) => {
    setBusy(true);
    axios
      .post(APICONSTANTS.issueFhqForProband, {
        user_id: probandId,
        withEmail,
        linkParameters
      })
      .then((res) => {
        const link = res.data?.token
          ? createUniqueLink(res.data.token)
          : res.data?.link || '';
        setGeneratedLink(link);
        setSnack({
          status: true,
          msg: withEmail
            ? t('Questionnaire sent')
            : t('Link generated successfully'),
          severity: 'success'
        });
        fetchStatus();
      })
      .catch((err) =>
        setSnack({
          status: true,
          msg: err?.response?.data?.error || t('An error has occurred!'),
          severity: 'error'
        })
      )
      .finally(() => setBusy(false));
  };

  const computeNodes = async (familyId) => {
    if (!familyId) return [];
    const resp = await axios.get(
      APICONSTANTS.getPedigreeDetails.replace(':familyId', familyId)
    );
    const { proband, families } = resp.data || {};
    if (!proband) return [];
    const ordered =
      validatePedigreeConfig({ user: proband, members: families || [] }) || [];
    const probandKey = String(proband._id || proband.pid || proband.uid);
    return ordered.map((n) => ({
      pid: String(n.pid),
      uid: n.uid != null ? String(n.uid) : String(n.pid),
      isDummy: !!n.isDummy,
      gender: n.gender || null,
      isProband: String(n.pid) === probandKey,
      fatherId: n.fatherId != null ? String(n.fatherId) : null,
      motherId: n.motherId != null ? String(n.motherId) : null,
      spouseIds: (n.spouseIds || []).map(String),
      childrenIds: (n.childrenIds || []).map(String)
    }));
  };

  const apply = async () => {
    setBusy(true);
    try {
      let fhqNodes = [];
      let realNodes = [];
      try {
        fhqNodes = await computeNodes(status?.shadowFamilyId);
      } catch (_) {
        fhqNodes = [];
      }
      try {
        realNodes = await computeNodes(status?.realFamilyId || user?.familyId);
      } catch (_) {
        realNodes = [];
      }

      const res = await axios.post(
        APICONSTANTS.applyFhq.replace(':probandId', probandId),
        { fhqNodes, realNodes }
      );
      const added = res.data?.created ?? 0;
      const merged = res.data?.updated ?? 0;
      const changed = added > 0 || merged > 0;
      setSnack({
        status: true,
        msg: changed
          ? t(
              'Applied to the pedigree ({{added}} added, {{merged}} updated). Refreshing…',
              { added, merged }
            )
          : t('No new questionnaire data to merge into the pedigree.'),
        severity: changed ? 'success' : 'info'
      });
      fetchStatus();
      if (changed) setTimeout(() => window.location.reload(), 1200);
      return true;
    } catch (err) {
      setSnack({
        status: true,
        msg: err?.response?.data?.error || t('An error has occurred!'),
        severity: 'error'
      });
      return false;
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    autoAppliedRef.current = false;
    setAutoApplyFailed(false);
    specialtiesTouchedRef.current = false;
  }, [probandId]);
  useEffect(() => {
    if (
      status?.autoApply &&
      canSend &&
      !autoAppliedRef.current &&
      !busy &&
      !autoApplyFailed
    ) {
      autoAppliedRef.current = true;
      apply().then((ok) => {
        if (!ok) setAutoApplyFailed(true);
      });
    }
  }, [status, canSend]);

  const discard = () => {
    setBusy(true);
    axios
      .post(APICONSTANTS.discardFhq.replace(':probandId', probandId))
      .then(() => {
        setSnack({
          status: true,
          msg: t('Submission discarded'),
          severity: 'success'
        });
        setGeneratedLink('');
        fetchStatus();
      })
      .catch((err) =>
        setSnack({
          status: true,
          msg: err?.response?.data?.error || t('An error has occurred!'),
          severity: 'error'
        })
      )
      .finally(() => setBusy(false));
  };

  // A questionnaire a negative stage 1 screen closed (TGCLOUD-1910), reopened as
  // the FULL questionnaire: the same link, re-sent; the screener is not asked
  // again.
  const sendFullQuestionnaire = () => {
    setBusy(true);
    axios
      .post(APICONSTANTS.revertToDraft, { id: status?.shadowProbandId })
      .then(() => {
        setSnack({
          status: true,
          msg: t('The full questionnaire has been sent to the patient.'),
          severity: 'success'
        });
        fetchStatus();
      })
      .catch((err) =>
        setSnack({
          status: true,
          msg: err?.response?.data?.error || t('An error has occurred!'),
          severity: 'error'
        })
      )
      .finally(() => setBusy(false));
  };

  const copy = () => {
    copyToClipBoard(generatedLink);
    setCopied(true);
    clearTimeout(copiedTimerRef.current);
    copiedTimerRef.current = setTimeout(() => setCopied(false), 1000);
  };

  if (!probandId) return null;

  if (loading) {
    return (
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={22} />
      </Box>
    );
  }

  if (!status) {
    return (
      <Grid sx={{ p: 1 }}>
        <ViewSubmissionButton
          to={`/admin/users/view/${probandId}`}
          label={t('View Family History')}
        />
      </Grid>
    );
  }

  if (status.createdFromFhq) {
    return (
      <Grid sx={{ p: 1 }}>
        <ViewSubmissionButton
          to={`/admin/users/view/${probandId}`}
          label={t('View Family History')}
        />
      </Grid>
    );
  }

  const label = status.fhqStatus || CONSTANTS.fhqStatus.NOT_SENT;
  const hasEmail = !!status.hasEmail || !!user?.email;
  const isNotSent = label === CONSTANTS.fhqStatus.NOT_SENT;
  const isSent = label === CONSTANTS.fhqStatus.SENT;
  const isSubmitted = label === CONSTANTS.fhqStatus.SUBMITTED;
  const isScreenedOut = label === CONSTANTS.fhqStatus.SCREENED_OUT;
  const canView = !!status.canViewFamilyHistory && status.shadowProbandId;

  return (
    <Grid container direction="column" spacing={1.5} sx={{ p: 1 }}>
      <Grid sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {t('FHQ Status')}:
        </Typography>
        <StatusChip label={label} />
      </Grid>

      {status.screening && (
        <Grid>
          <ScreeningSummary screening={status.screening} />
        </Grid>
      )}

      {isScreenedOut && (
        <>
          <Grid>
            <Alert severity="info">
              {t(
                'The patient’s answers to the screening questions did not call for the full family history questionnaire, so the questionnaire was closed and nothing was added to the pedigree.'
              )}
            </Alert>
          </Grid>
          {canSend && status.canSendFullQuestionnaire && (
            <Grid>
              <Button
                variant="contained"
                startIcon={
                  busy ? <CircularProgress size={14} /> : <EmailIcon />
                }
                disabled={busy}
                onClick={sendFullQuestionnaire}
              >
                {t('Send the full questionnaire')}
              </Button>
            </Grid>
          )}
        </>
      )}

      {canView && (
        <Grid>
          <ViewSubmissionButton
            to={`/admin/users/view/${status.shadowProbandId}`}
            label={t('View Family History')}
            fullWidth
          />
        </Grid>
      )}
      {isSubmitted &&
        canSend &&
        (status.autoApply && !autoApplyFailed ? (
          <Grid sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={16} />
            <Typography variant="body2">
              {t('Applying the questionnaire to the pedigree…')}
            </Typography>
          </Grid>
        ) : (
          <>
            <Grid>
              <Alert severity="info">
                {t(
                  'The patient has submitted their questionnaire. Review it above, then Apply to merge it into the pedigree, or Discard to remove it.'
                )}
              </Alert>
            </Grid>
            <Grid sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                disabled={busy}
                onClick={apply}
                startIcon={busy ? <CircularProgress size={14} /> : null}
              >
                {t('Apply')}
              </Button>
              <Button
                variant="outlined"
                color="error"
                disabled={busy}
                onClick={discard}
              >
                {t('Discard')}
              </Button>
            </Grid>
          </>
        ))}

      {(isNotSent || isSent) && (
        <>
          {isSent && (
            <Grid>
              <Alert severity="info">
                {t(
                  'A questionnaire link has been sent and is awaiting the patient’s response. You can resend it or generate a fresh link below.'
                )}
              </Alert>
            </Grid>
          )}
          {canSend ? (
            <>
              {!hasEmail && (
                <Grid>
                  <Alert severity="info">
                    {t(
                      'Select the proband, add an email in the Person section and save to generate link.'
                    )}
                  </Alert>
                </Grid>
              )}
              {/* Without this the send always fell back to whatever specialties
                  the proband record happened to carry, so a clinician working
                  from the pedigree could not choose them — the questionnaire went
                  out with the default set. Same picker (and the same option
                  source) as the Generate Link screen. */}
              <Grid sx={{ minWidth: 0 }}>
                <FormLabel component="label" className="form-label">
                  {t('Specialty questions')}
                </FormLabel>
                <TextInput
                  type="multiple-select"
                  name="linkParameters"
                  value={linkParameters}
                  onChange={handleSpecialtyChange}
                  options={specialtyOptions}
                  mapper={specialtyLabels}
                  disabled={busy}
                  explicitSelection
                />
                <Typography
                  variant="caption"
                  sx={{ color: '#6b7a8d', mt: 0.5, display: 'block' }}
                >
                  {isSent
                    ? t(
                        'Resending applies these specialties to the existing link.'
                      )
                    : t(
                        'Select one or more specialties to include targeted questions.'
                      )}
                </Typography>
              </Grid>
              <Grid sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  startIcon={
                    busy ? <CircularProgress size={14} /> : <EmailIcon />
                  }
                  disabled={busy || !hasEmail}
                  onClick={() => issue(true)}
                >
                  {isSent ? t('Resend link') : t('Generate and send link')}
                </Button>
              </Grid>
            </>
          ) : (
            fhqAllowed && (
              <Grid>
                <Typography variant="caption" sx={{ color: '#6b7a8d' }}>
                  {t('Read-only: you cannot send questionnaires.')}
                </Typography>
              </Grid>
            )
          )}
          {generatedLink && (
            <Grid sx={{ minWidth: 0 }}>
              <TextField
                size="small"
                fullWidth
                variant="outlined"
                value={generatedLink}
                title={generatedLink}
                slotProps={{
                  input: {
                    readOnly: true,
                    sx: { fontFamily: 'monospace', fontSize: 12, pr: 0.5 },
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title={copied ? t('Copied!') : t('Copy link')}>
                          <IconButton size="small" onClick={copy} edge="end">
                            <FileCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    )
                  }
                }}
              />
            </Grid>
          )}
        </>
      )}
    </Grid>
  );
};

export default FamilyHistoryMenu;
